/**
 * DevPharmacyQuoteClaimsScreen
 *
 * Empirical inspector for the dump_anything ingestion pipeline. Upload a real
 * pharmacy quote (PDF or photo), run it through processSource, and dump the
 * raw claim fieldPaths plus the projector's output side by side.
 *
 * Answers "does the model emit quote.lineItems[N].amount or medications[N].*
 * for a pharmacy quote?" without touching the production PharmacyQuoteCaptureModal.
 *
 * Behind the existing dev-tools gate.
 */

import { useState } from "react";
import { Pressable, ScrollView, View, TextInput } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { ChevronLeft, Camera, FileText, Copy } from "lucide-react-native";
import * as Clipboard from "expo-clipboard";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import { MoonText } from "@louise/moon-ui/primitives/MoonText";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  colors,
  spacing,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { useDocumentSourceTransfer } from "@src/packages/documents/ingestion/useDocumentSourceTransfer";
import {
  projectDocumentProcessingRunToPrefill,
  type DocumentProcessingOutcome,
} from "@src/packages/documents/ingestion/documentProcessingRun";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

type SelectedFile = {
  uri: string;
  name: string;
  mimeType: string;
  source: "camera" | "picker";
};

type Phase =
  | { kind: "idle" }
  | { kind: "picking"; source: "camera" | "picker" }
  | { kind: "uploading" }
  | { kind: "processing" }
  | { kind: "done"; outcome: DocumentProcessingOutcome }
  | { kind: "error"; message: string };

export default function DevPharmacyQuoteClaimsScreen() {
  const navigation = useNavigation();
  const transfer = useDocumentSourceTransfer();
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const pickFile = async (source: "camera" | "picker") => {
    setPhase({ kind: "picking", source });
    try {
      if (source === "camera") {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) {
          setPhase({ kind: "idle" });
          return;
        }
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: ["images"],
          quality: 0.85,
        });
        if (result.canceled || !result.assets?.[0]) {
          setPhase({ kind: "idle" });
          return;
        }
        const asset = result.assets[0];
        const name = `pharmacy-quote-${Date.now()}.jpg`;
        setFile({
          uri: asset.uri,
          name,
          mimeType: resolveUploadContentType({
            name,
            uri: asset.uri,
            mimeType: asset.mimeType,
          }),
          source,
        });
      } else {
        const result = await DocumentPicker.getDocumentAsync({
          type: ["application/pdf", "image/*"],
          copyToCacheDirectory: true,
        });
        if (result.canceled || !result.assets?.[0]) {
          setPhase({ kind: "idle" });
          return;
        }
        const asset = result.assets[0];
        setFile({
          uri: asset.uri,
          name: asset.name,
          mimeType: resolveUploadContentType({
            name: asset.name,
            uri: asset.uri,
            mimeType: asset.mimeType,
          }),
          source,
        });
      }
      setPhase({ kind: "idle" });
    } catch (error) {
      setPhase({
        kind: "error",
        message: error instanceof Error ? error.message : "Selection failed",
      });
    }
  };

  const runInspection = async () => {
    if (!file) return;
    setPhase({ kind: "uploading" });
    try {
      const source = await transfer.uploadSource(
        {
          uri: file.uri,
          name: file.name,
          mimeType: file.mimeType,
        },
        {
          destination: "document",
          sourceContext: {
            surface: "dev_pharmacy_quote_claims_inspector",
            route: "DevPharmacyQuoteClaims",
            form: "pharmacy_quote",
            targetHints: ["pharmacy_quote"],
            allowBackgroundProcessing: false,
          },
        },
      );
      setPhase({ kind: "processing" });
      const outcome = await transfer.processSource(source, {
        attempts: 40,
        intervalMs: 1_500,
      });
      setPhase({ kind: "done", outcome });
    } catch (error) {
      setPhase({
        kind: "error",
        message: error instanceof Error ? error.message : "Processing failed",
      });
    }
  };

  const reset = () => {
    setFile(null);
    setPhase({ kind: "idle" });
    transfer.reset();
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.md,
          paddingBottom: spacing.xl * 2,
          gap: spacing.lg,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Pressable
          onPress={() => navigation.goBack()}
          hitSlop={12}
          style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
        >
          <ChevronLeft size={18} color={colors.textMid} />
          <MoonText variant="body-sm" color="textMid">
            Back
          </MoonText>
        </Pressable>

        <View style={{ gap: spacing.xs }}>
          <MoonText variant="display">Pharmacy Quote Claims Inspector</MoonText>
          <MoonText variant="body-sm" color="textLight">
            Upload a real pharmacy quote PDF or photo. Runs uploadSource +
            processSource against dev backend and dumps every claim fieldPath /
            rawValue plus the projector output. No production data is written.
          </MoonText>
        </View>

        <View style={{ gap: spacing.sm }}>
          <MoonText variant="section-header" color="textMid">
            1. Pick a file
          </MoonText>
          <View style={{ flexDirection: "row", gap: spacing.sm }}>
            <SourceButton
              icon={Camera}
              label="Photo"
              active={file?.source === "camera"}
              disabled={
                phase.kind === "uploading" || phase.kind === "processing"
              }
              onPress={() => void pickFile("camera")}
            />
            <SourceButton
              icon={FileText}
              label="PDF / image"
              active={file?.source === "picker"}
              disabled={
                phase.kind === "uploading" || phase.kind === "processing"
              }
              onPress={() => void pickFile("picker")}
            />
          </View>
          {file ? (
            <View
              style={{
                padding: spacing.sm,
                borderRadius: 12,
                backgroundColor: "rgba(0,0,0,0.04)",
              }}
            >
              <MoonText variant="body-sm" color="textMid">
                {file.name}
              </MoonText>
              <MoonText variant="body-sm" color="textFaint">
                {file.mimeType}
              </MoonText>
            </View>
          ) : null}
        </View>

        <View style={{ gap: spacing.sm }}>
          <MoonText variant="section-header" color="textMid">
            2. Run
          </MoonText>
          <Pressable
            onPress={() => void runInspection()}
            disabled={
              !file || phase.kind === "uploading" || phase.kind === "processing"
            }
            style={{
              padding: spacing.md,
              borderRadius: 14,
              backgroundColor:
                !file ||
                phase.kind === "uploading" ||
                phase.kind === "processing"
                  ? "rgba(0,0,0,0.06)"
                  : colors.softBrownMid,
              alignItems: "center",
            }}
          >
            <MoonText
              variant="title"
              color={
                !file ||
                phase.kind === "uploading" ||
                phase.kind === "processing"
                  ? "textFaint"
                  : "surface"
              }
            >
              {phase.kind === "uploading"
                ? "Uploading…"
                : phase.kind === "processing"
                  ? "Extracting claims…"
                  : "Upload & inspect claims"}
            </MoonText>
          </Pressable>
          <PhaseLine phase={phase} />
        </View>

        {phase.kind === "done" ? (
          <ResultView outcome={phase.outcome} sourceName={file?.name ?? ""} />
        ) : null}

        {phase.kind === "done" || phase.kind === "error" ? (
          <Pressable
            onPress={reset}
            style={{
              padding: spacing.sm,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.1)",
              alignItems: "center",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Reset and try another file
            </MoonText>
          </Pressable>
        ) : null}
      </ScrollView>
    </GlassScreenLayout>
  );
}

function SourceButton({
  icon: Icon,
  label,
  active,
  disabled,
  onPress,
}: {
  icon: typeof Camera;
  label: string;
  active: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={{
        flex: 1,
        padding: spacing.md,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: active ? colors.softBrownMid : "rgba(0,0,0,0.1)",
        backgroundColor: active ? "rgba(0,0,0,0.04)" : "transparent",
        alignItems: "center",
        gap: spacing.xs,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Icon size={20} color={colors.textMid} strokeWidth={1.8} />
      <MoonText variant="body-sm" color="textMid">
        {label}
      </MoonText>
    </Pressable>
  );
}

function PhaseLine({ phase }: { phase: Phase }) {
  if (phase.kind === "idle" || phase.kind === "picking") return null;
  if (phase.kind === "error") {
    return (
      <View
        style={{
          padding: spacing.sm,
          borderRadius: 10,
          backgroundColor: "rgba(200, 60, 60, 0.08)",
        }}
      >
        <MoonText variant="body-sm" color="textMid">
          Error: {phase.message}
        </MoonText>
      </View>
    );
  }
  return null;
}

function ResultView({
  outcome,
  sourceName,
}: {
  outcome: DocumentProcessingOutcome;
  sourceName: string;
}) {
  const { run } = outcome;
  const projected = projectDocumentProcessingRunToPrefill(run, sourceName);
  const fieldPathsOnly = run.claims.map((c) => c.fieldPath).join("\n");
  const fullJson = JSON.stringify(
    {
      runId: run.id,
      status: run.status,
      classificationCode: run.classificationCode,
      completeness: run.completeness,
      omittedReason: run.omittedReason,
      partiesCount: run.parties.length,
      parties: run.parties.map((p) => ({
        role: p.role,
        rawName: p.rawName,
        normalizedName: p.normalizedName,
      })),
      claims: run.claims.map((c) => ({
        fieldPath: c.fieldPath,
        rawValue: c.rawValue,
        rawDate: c.rawDate,
        kind: c.kind,
        confidence: c.confidence,
        status: c.status,
        normalizedValue: c.normalizedValue,
      })),
      projected: {
        quote: projected.quote,
        medications: projected.medications ?? [],
        clinicName: projected.clinicName,
        providerName: projected.providerName,
      },
    },
    null,
    2,
  );

  return (
    <View style={{ gap: spacing.lg }}>
      <View style={{ gap: spacing.xs }}>
        <MoonText variant="section-header" color="textMid">
          3. Run metadata
        </MoonText>
        <MetaRow label="Run id" value={run.id} />
        <MetaRow label="Status" value={run.status} />
        <MetaRow label="Classification" value={run.classificationCode ?? "—"} />
        <MetaRow label="Completeness" value={String(run.completeness)} />
        <MetaRow label="Omitted reason" value={run.omittedReason ?? "—"} />
        <MetaRow label="Claims count" value={String(run.claims.length)} />
        <MetaRow label="Parties count" value={String(run.parties.length)} />
      </View>

      <View style={{ gap: spacing.xs }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MoonText variant="section-header" color="textMid">
            4. Raw claims
          </MoonText>
          <CopyButton label="Copy fieldPaths" value={fieldPathsOnly} />
        </View>
        {run.claims.length === 0 ? (
          <MoonText variant="body-sm" color="textFaint">
            No claims emitted.
          </MoonText>
        ) : (
          run.claims.map((claim, index) => (
            <ClaimRow key={claim.id ?? index} index={index} claim={claim} />
          ))
        )}
      </View>

      <View style={{ gap: spacing.xs }}>
        <MoonText variant="section-header" color="textMid">
          5. Projected quote (client-side)
        </MoonText>
        <MetaRow label="clinicName" value={projected.quote.clinicName || "—"} />
        <MetaRow label="quoteDate" value={projected.quote.quoteDate || "—"} />
        <MetaRow label="currency" value={projected.quote.currency || "—"} />
        <MetaRow
          label="totalAmount"
          value={projected.quote.totalAmount || "—"}
        />
        <MoonText variant="body-sm" color="textMid" style={{ marginTop: 4 }}>
          Line items ({projected.quote.lineItems.length}):
        </MoonText>
        {projected.quote.lineItems.length === 0 ? (
          <MoonText variant="body-sm" color="textFaint">
            (empty — projector did not find any lineItems[N] / charges[N] /
            fees[N] group)
          </MoonText>
        ) : (
          projected.quote.lineItems.map((item, index) => (
            <View
              key={index}
              style={{
                padding: spacing.sm,
                borderRadius: 8,
                backgroundColor: "rgba(0,0,0,0.03)",
                gap: 2,
              }}
            >
              <MoonText variant="body-sm" color="textMid">
                #{index} label: {item.label || "—"}
              </MoonText>
              <MoonText variant="body-sm" color="textLight">
                amount: {item.amount || "—"} · category: {item.category || "—"}
              </MoonText>
              {item.notes ? (
                <MoonText variant="body-sm" color="textFaint">
                  notes: {item.notes}
                </MoonText>
              ) : null}
            </View>
          ))
        )}
      </View>

      {projected.medications && projected.medications.length > 0 ? (
        <View style={{ gap: spacing.xs }}>
          <MoonText variant="section-header" color="textMid">
            6. Projected medications (fallback signal)
          </MoonText>
          <MoonText variant="body-sm" color="textFaint">
            If lineItems is empty but medications is populated, the model
            emitted medication[N].* fieldPaths — that's the flat fallback the
            backend promotion needs to reassemble.
          </MoonText>
          {projected.medications.map((med, index) => (
            <View
              key={index}
              style={{
                padding: spacing.sm,
                borderRadius: 8,
                backgroundColor: "rgba(0,0,0,0.03)",
              }}
            >
              <MoonText variant="body-sm" color="textMid">
                #{index} {med.medicationName || "—"}
              </MoonText>
              <MoonText variant="body-sm" color="textLight">
                dosage: {med.dosage || "—"} {med.dosageUnit}
              </MoonText>
            </View>
          ))}
        </View>
      ) : null}

      <View style={{ gap: spacing.xs }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MoonText variant="section-header" color="textMid">
            7. Full JSON
          </MoonText>
          <CopyButton label="Copy JSON" value={fullJson} />
        </View>
        <TextInput
          value={fullJson}
          multiline
          editable={false}
          style={{
            padding: spacing.sm,
            borderRadius: 10,
            backgroundColor: "rgba(0,0,0,0.04)",
            color: colors.textMid,
            fontSize: 11,
            fontFamily: "Courier",
            minHeight: 240,
          }}
        />
      </View>
    </View>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: "row", gap: spacing.sm }}>
      <MoonText variant="body-sm" color="textFaint" style={{ minWidth: 130 }}>
        {label}
      </MoonText>
      <MoonText variant="body-sm" color="textMid" style={{ flex: 1 }}>
        {value}
      </MoonText>
    </View>
  );
}

function ClaimRow({
  index,
  claim,
}: {
  index: number;
  claim: DocumentProcessingOutcome["run"]["claims"][number];
}) {
  return (
    <View
      style={{
        padding: spacing.sm,
        borderRadius: 8,
        backgroundColor: "rgba(0,0,0,0.03)",
        gap: 2,
      }}
    >
      <MoonText variant="body-sm" color="textMid">
        #{index} [{claim.kind}] {claim.fieldPath}
      </MoonText>
      <MoonText variant="body-sm" color="textLight">
        {claim.rawValue || "(empty)"}
      </MoonText>
      <MoonText variant="body-sm" color="textFaint">
        conf {claim.confidence?.toFixed(2) ?? "—"} · status {claim.status}
      </MoonText>
    </View>
  );
}

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Pressable
      onPress={async () => {
        await Clipboard.setStringAsync(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: spacing.sm,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: "rgba(0,0,0,0.06)",
      }}
    >
      <Copy size={12} color={colors.textMid} strokeWidth={2} />
      <MoonText variant="body-sm" color="textMid">
        {copied ? "Copied" : label}
      </MoonText>
    </Pressable>
  );
}
